import { memo, ReactElement, useContext, useEffect, useState } from 'react';
import {
    DisplayedQuestions,
    OnSpotAnswer,
    OnSpotContextProps,
    OnSpotQuestion,
    OnSpotQuestions,
    OnSpotReturnTree,
    OnSpotReturnTreeAnswer,
} from '@/model/onSpot/OnSpot';
import * as storage from '@/_helper/storage';
import { buildTreeFromArrays } from '../../_helper/_helper';
import ConditionalWrapper from '../../comp/ConditionalWrapper';
import Question from './Question';
import { OnSpotContext } from '../../index';
import { TreeNode } from '@/model/onSpot/TreeNodes';
import tracking from '@/_helper/tracking';

interface TreeSectionProps {
    saveDisplayedQuestions: (displayedQuestions: DisplayedQuestions) => void;
}

export const TreeSection = ({ saveDisplayedQuestions }: TreeSectionProps) => {
    const context = useContext(OnSpotContext);
    const { treeSectionId, treeSectionMapping, setTreeSectionMapping, dataReturnTree, dataTree, setDataReturnTree } =
        context as OnSpotContextProps;
    const showedQuestions = (context?.displayedQuestions as DisplayedQuestions) ?? {};
    const [displayedQuestions, setDisplayedQuestions] = useState<DisplayedQuestions>(showedQuestions);

    useEffect(() => {
        saveDisplayedQuestions(displayedQuestions);
    }, [displayedQuestions, saveDisplayedQuestions]);

    const setAnswersToReturnTree = (
        questionRef: string,
        onSpotAnswers: OnSpotAnswer[],
        data: OnSpotQuestions | undefined = dataTree
    ) => {
        let newArr: OnSpotReturnTree = { ...dataReturnTree };
        if (data && data[questionRef]) {
            if (!newArr[questionRef]) {
                newArr[questionRef] = {
                    questionId: data[questionRef].id,
                    dbId: data[questionRef].dbId,
                    answers: [],
                };
            }

            onSpotAnswers.forEach((onSpotAnswer) => {
                data[questionRef].answers.forEach((answer) => {
                    if (onSpotAnswer.id === answer.id) {
                        const newAnswer: OnSpotReturnTreeAnswer = {
                            id: onSpotAnswer.id,
                            dbId: onSpotAnswer.dbId,
                        };
                        if (answer.value) {
                            newAnswer.freeText = answer.value;
                        }
                        if (answer.$refId) {
                            newAnswer.$refId = answer.$refId;
                        }
                        if (
                            newArr[questionRef].answers.length > 0 &&
                            newArr[questionRef].answers.findIndex((a) => {
                                return a.id === answer.id;
                            }) !== -1
                        ) {
                            const index = newArr[questionRef].answers.findIndex((a) => a.id === answer.id);
                            newArr[questionRef].answers[index] = newAnswer;
                        } else {
                            newArr[questionRef].answers.push(newAnswer);
                        }
                    }
                });
            });
        }
        return newArr;
    };

    const handleChange = (question: OnSpotQuestion, answer: OnSpotAnswer, treeSectionId: string, value?: string) => {
        tracking('interaction', 'onSpot');
        if (question.type === 'singleChoice') {
            handleRadioChange(question, answer, treeSectionId);
        }
        if (question.type === 'multipleChoice') {
            handleCheckboxChange(question, answer, treeSectionId);
        }
        if (question.type === 'input') {
            answer.value = value;
            handleInputChange(question, answer, treeSectionId);
        }
    };

    const handleRadioChange = (question: OnSpotQuestion, answer: OnSpotAnswer, treeSectionId: string) => {
        if (!dataTree || !dataReturnTree) {
            return false;
        }

        const removeRecursive = (
            questionId: string,
            treeSectionId: string,
            displayedQuestions: DisplayedQuestions,
            returnTree: OnSpotReturnTree
        ) => {
            returnTree['question-' + questionId]?.answers.forEach((answer, index) => {
                if (returnTree && answer.$refId) {
                    const tmpQuestionId = dataTree[answer.$refId].id;
                    if (returnTree[answer.$refId] || index > -1) {
                        removeRecursive(tmpQuestionId.toString(), treeSectionId, displayedQuestions, returnTree);
                    }

                    Object.keys(displayedQuestions).forEach((treeSection) => {
                        displayedQuestions[treeSection].forEach((question, index) => {
                            if (question.id === tmpQuestionId) {
                                resetAnswerChoices(question);

                                if (displayedQuestions[treeSection].length > 1) {
                                    displayedQuestions[treeSection] = displayedQuestions[treeSection].filter(
                                        (_q, qIndex) => qIndex !== index
                                    );
                                } else {
                                    delete displayedQuestions[treeSection];
                                }
                            }
                        });
                    });
                }
            });
            delete returnTree['question-' + questionId];
            return { displayedQuestions, returnTree };
        };

        let newDisplayedQuestions = { ...displayedQuestions };

        // is change or is new
        if (newDisplayedQuestions && dataReturnTree['question-' + question.id]) {
            const removedQuestions = removeRecursive(question.id, treeSectionId, displayedQuestions, dataReturnTree);
            if (!removedQuestions.displayedQuestions[treeSectionId]) {
                removedQuestions.displayedQuestions[treeSectionId] = [];
            }
            removedQuestions.displayedQuestions[treeSectionId].push(structuredClone(dataTree[answer.$refId]));
            newDisplayedQuestions = structuredClone({ ...removedQuestions.displayedQuestions });
            setDataReturnTree(removedQuestions.returnTree);
        } else {
            if (!newDisplayedQuestions[treeSectionId]) {
                newDisplayedQuestions[treeSectionId] = [];
            }
            newDisplayedQuestions[treeSectionId].push(structuredClone(dataTree[answer.$refId]));
        }
        setDisplayedQuestions(newDisplayedQuestions);
        const name = 'question-' + question.id;
        let newReturnTree = setAnswersToReturnTree(name, [answer]);
        if (dataTree[answer.$refId]?.type === 'input') {
            newReturnTree = { ...newReturnTree, ...setNextQuestionAutomatic(answer.$refId) };
        }
        if (newReturnTree) {
            setDataReturnTree({ ...dataReturnTree, ...newReturnTree });
        }
    };

    const handleCheckboxChange = (question: OnSpotQuestion, answer: OnSpotAnswer, treeSectionId: string) => {
        if (!dataTree || !dataReturnTree) {
            return false;
        }

        const removeRecursive = (
            questionId: string,
            answer: OnSpotAnswer | OnSpotReturnTreeAnswer,
            displayedQuestions: DisplayedQuestions,
            returnTree: OnSpotReturnTree
        ) => {
            returnTree['question-' + questionId]?.answers.forEach((tmpAnswer) => {
                if (answer.id === tmpAnswer.id) {
                    // prüfen, ob es weitere Fragen gibt → rekursiv löschen
                    if (answer.$refId) {
                        const newQuestionId = dataTree[answer.$refId].id;
                        returnTree['question-' + newQuestionId]?.answers.forEach((newTmpAnswer) => {
                            removeRecursive(newQuestionId.toString(), newTmpAnswer, displayedQuestions, returnTree);
                        });
                    }

                    // antwort entfernen
                    if (returnTree['question-' + questionId].answers.length > 1) {
                        returnTree['question-' + questionId].answers = returnTree[
                            'question-' + questionId
                        ].answers.filter((a) => a.id !== answer.id);
                    } else {
                        delete returnTree['question-' + questionId];
                    }

                    // displayedQuestions entfernen
                    if (answer.$refId) {
                        const nextQuestion = dataTree[answer.$refId];
                        Object.keys(displayedQuestions).forEach((treeSection) => {
                            displayedQuestions[treeSection].forEach((question) => {
                                if (question.id === nextQuestion.id && treeSection.includes(answer.id.toString())) {
                                    const questionIndex = displayedQuestions[treeSection].findIndex(
                                        (q) => q.id === question.id
                                    );
                                    resetAnswerChoices(question);
                                    if (displayedQuestions[treeSection].length > 1 && questionIndex) {
                                        displayedQuestions[treeSection] = displayedQuestions[treeSection].filter(
                                            (q) => q.id === question.id
                                        );
                                    } else {
                                        delete displayedQuestions[treeSection];
                                    }
                                }
                            });
                        });
                    }
                }
            });
            return { displayedQuestions, returnTree };
        };

        let newDisplayedQuestions = { ...displayedQuestions };
        if (answer.checked) {
            const newTreeSectionId = `${treeSectionId}_${answer.id}`;
            if (!newDisplayedQuestions[newTreeSectionId]) {
                newDisplayedQuestions[newTreeSectionId] = [];
            }
            const newTreeSection = { ...treeSectionMapping, [newTreeSectionId]: answer.label };
            setTreeSectionMapping(newTreeSection);
            newDisplayedQuestions[newTreeSectionId].push(structuredClone(dataTree[answer.$refId]));
            const newReturnTreeName = 'question-' + question.id;
            let newReturnTree = {
                ...dataReturnTree,
                ...setAnswersToReturnTree(newReturnTreeName, [answer]),
            };
            if (dataTree[answer.$refId]?.type === 'input') {
                newReturnTree = { ...newReturnTree, ...setNextQuestionAutomatic(answer.$refId) };
            }
            setDataReturnTree({ ...dataReturnTree, ...newReturnTree });
        } else {
            // remove answer recursive from returnTree
            const removedQuestions = removeRecursive(question.id, answer, displayedQuestions, dataReturnTree);
            newDisplayedQuestions = { ...removedQuestions.displayedQuestions };
            setDataReturnTree({ ...removedQuestions.returnTree });
        }

        setDisplayedQuestions({ ...newDisplayedQuestions });
    };

    const handleInputChange = (question: OnSpotQuestion, answer: OnSpotAnswer, treeSectionId: string) => {
        let newReturnTree = { ...dataReturnTree };

        if (!newReturnTree['question-' + question.id]) {
            newReturnTree['question-' + question.id] = {
                questionId: question.id,
                dbId: question.dbId,
                answers: [],
            };
        }

        if (
            newReturnTree['question-' + question.id].answers.findIndex((a) => {
                return a.id === answer.id;
            }) !== -1
        ) {
            const index = newReturnTree['question-' + question.id].answers.findIndex((a) => a.id === answer.id);
            newReturnTree['question-' + question.id].answers[index].freeText = answer.value;
        } else {
            let newAnswer: OnSpotReturnTreeAnswer = {
                id: answer.id,
                dbId: answer.dbId,
                freeText: answer.value,
            };
            if (answer.$refId) {
                newAnswer.$refId = answer.$refId;
            }
            newReturnTree['question-' + question.id].answers.push(newAnswer);
        }

        if (dataTree[answer.$refId]?.type === 'input') {
            newReturnTree = { ...newReturnTree, ...setNextQuestionAutomatic(answer.$refId) };
        }

        // next question
        if (answer.$refId && !displayedQuestions[treeSectionId].find((q) => q.id === dataTree[answer.$refId].id)) {
            const newDisplayedQuestions = { ...displayedQuestions };
            if (!newDisplayedQuestions[treeSectionId]) {
                newDisplayedQuestions[treeSectionId] = [];
            }
            newDisplayedQuestions[treeSectionId].push(structuredClone(dataTree[answer.$refId]));
            setDisplayedQuestions(newDisplayedQuestions);
        }
        setDataReturnTree(newReturnTree);
    };

    const setNextQuestionAutomatic = (nextRefId: string) => {
        if (dataTree[nextRefId]) {
            const nextAnswers = dataTree[nextRefId].answers.map((a) => a);
            const newReturnTreeName = 'question-' + dataTree[nextRefId].id;
            const newReturnTreeNode = setAnswersToReturnTree(newReturnTreeName, nextAnswers);
            if (newReturnTreeNode) {
                return newReturnTreeNode;
            }
        }
        return null;
    };

    const resetAnswerChoices = (question: OnSpotQuestion) => {
        if (question.type === 'input') {
            question.answers.forEach((answer) => {
                answer.value = '';
            });
        } else if (question.type === 'multipleChoice' || question.type === 'singleChoice') {
            question.answers.forEach((answer) => {
                answer.checked = false;
            });
        }
    };

    const tree = (nodes: TreeNode) => {
        const elements: ReactElement[] = [];
        if (nodes) {
            if (nodes.children.length > 0) {
                nodes.children.forEach((node) => {
                    const splittedTreeIds = node.treeId.split('_');

                    elements.push(
                        <ConditionalWrapper
                            classList={`${splittedTreeIds.length >= 3 ? 'sub-entry' : 'entry'}`}
                            key={`tree-${node.id}`}
                            condition={splittedTreeIds.length <= 3}>
                            {nodes.treeId !== treeSectionId ? (
                                <div className="container">{tree(node)}</div>
                            ) : (
                                tree(node)
                            )}
                        </ConditionalWrapper>
                    );
                });
            }
        }

        return (
            <>
                <ConditionalWrapper key={`t-${nodes.treeId}`} condition={nodes.treeId === treeSectionId}>
                    <div className="container">
                        {nodes.treeId !== treeSectionId && (
                            <h2 className="color-primary">{treeSectionMapping[nodes?.treeId]}</h2>
                        )}
                        {displayedQuestions[nodes?.treeId]?.map((question: OnSpotQuestion, index) => {
                            if (!question) {
                                throw new Error('question not found');
                            } else {
                                return (
                                    <Question
                                        treeSectionId={nodes.treeId}
                                        key={`tree-question-${nodes.treeId}-${question?.id}-${index}`}
                                        question={question}
                                        handleChange={handleChange}
                                    />
                                );
                            }
                        })}
                    </div>
                </ConditionalWrapper>
                {elements.length > 0 && elements}
            </>
        );
    };

    // Baumstruktur erstellen
    if (dataTree['entrypoint'] && treeSectionId) {
        //setTreeSectionId(dataTree['entrypoint'].id.toString());
        let newDisplayedQuestions = { ...displayedQuestions };
        if (Object.keys(newDisplayedQuestions).length < 1) {
            // TODO: from storage? delete or fix
            if (storage.get('displayedQuestions') && storage.get('returnTree')) {
                newDisplayedQuestions = storage.get('displayedQuestions');
                setDataReturnTree(storage.get('returnTree'));
            } else {
                newDisplayedQuestions = {
                    ...newDisplayedQuestions,
                    [dataTree['entrypoint'].id]: [dataTree['entrypoint']],
                };
            }
            setDisplayedQuestions(newDisplayedQuestions);
        }
        const array = Object.keys(newDisplayedQuestions).map((key) => {
            return key;
        });
        const treeArray = buildTreeFromArrays(array);
        return <>{displayedQuestions && treeArray ? tree(treeArray) : null}</>;
    }
    return null;
};

export default memo(TreeSection);
